/**
 * Shared packing slip print utility
 * Used by the PSP portal, Admin → Packing Slips and Admin → Operations.
 *
 * Phase 12e: one order per sheet (page break between orders) with the slip
 * header repeated on every sheet, an optional PSP shipment block per order
 * (PSP, shipment group, tracking, batch, PSP slip data), a checkbox column
 * and a receiving section. Same format everywhere.
 *
 * @param {Array}  items       - line items to include (each with .order)
 * @param {string} slipId      - packing slip ID (e.g. PS-00001)
 * @param {string} createdAt   - original creation date (ISO string) — shown as "Created"
 * @param {object} opts        - optional:
 *        orderInfo  { [orderNumber]: { pspNames, shipmentGroups, tracking, batches, slipData, notOnSlip } }
 *        reprint    true → "REPRINT" marker
 *        win        a window already opened by the caller (avoids pop-up blockers after async calls)
 *        autoPrint  false → just show the slip (View), don't open the print dialog (Phase 12f)
 */
const esc = (v) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

function slipDataLines(data) {
  if (data == null) return [];
  if (typeof data !== 'object') return [esc(data)];
  const lines = [];
  const walk = (obj, prefix) => {
    for (const [k, v] of Object.entries(obj)) {
      const key = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === 'object' && !Array.isArray(v)) walk(v, key);
      else lines.push(`<strong>${esc(key)}:</strong> ${esc(Array.isArray(v) ? v.map(x => (typeof x === 'object' ? JSON.stringify(x) : x)).join(', ') : v)}`);
    }
  };
  walk(data, '');
  return lines.slice(0, 40);
}

export function printPackingSlip(items, slipId, createdAt, opts = {}) {
  const win = opts.win || window.open('', '_blank');
  if (!win) return;
  const orderInfo = opts.orderInfo || {};

  // Group items by order number
  const byOrder = {};
  items.forEach(item => {
    const orderNum = item.order?.orderNumber || 'Unknown';
    if (!byOrder[orderNum]) byOrder[orderNum] = { order: item.order, items: [] };
    byOrder[orderNum].items.push(item);
  });
  const orderCount = Object.keys(byOrder).length;

  const createdStr = createdAt ? new Date(createdAt).toLocaleString() : null;
  const printedStr = new Date().toLocaleString();

  const slipHeader = (idx) => `
    <div class="slip-header">
      <div>
        <h1 style="margin:0 0 4px;font-size:20px;">Packing Slip${opts.reprint ? ' <span class="reprint">REPRINT</span>' : ''}</h1>
        <div style="font-size:13px;font-weight:600;color:#333;">${esc(slipId)}</div>
        ${createdStr ? `<div class="muted">Created: ${esc(createdStr)}</div>` : ''}
        <div class="muted">Printed: ${esc(printedStr)}</div>
        <div class="muted">Order ${idx + 1} of ${orderCount} · ${items.length} item${items.length !== 1 ? 's' : ''} on this slip</div>
      </div>
      <div style="text-align:center;">
        <svg class="slip-barcode"></svg>
        <div style="font-size:10px;color:#666;margin-top:2px;">${esc(slipId)}</div>
      </div>
    </div>`;

  const pages = Object.entries(byOrder).map(([orderNum, group], idx) => {
    const order = group.order || {};
    const info = orderInfo[orderNum];
    const customer = [order.customerFirstName, order.customerLastName].filter(Boolean).join(' ');

    const itemRows = group.items.map(item => {
      const photo = item.photoUrl || item.photoOneDrivePath
        ? `<img src="/api/uploads/image?path=${encodeURIComponent(item.photoOneDrivePath || '')}"
             style="width:60px;height:60px;object-fit:contain;border:1px solid #ddd;border-radius:4px;"
             onerror="this.style.display='none'" />`
        : '<div style="width:60px;height:60px;background:#f5f5f5;border:1px solid #ddd;border-radius:4px;display:flex;align-items:center;justify-content:center;font-size:10px;color:#999;">No photo</div>';
      return `
        <tr>
          <td style="text-align:center;width:28px;"><span class="box"></span></td>
          <td style="text-align:center;">${photo}</td>
          <td style="font-family:monospace;font-size:11px;">${esc(item.lineItemDescription)}</td>
          <td>${esc(item.productName)}</td>
          <td>${esc(item.sku)}</td>
          <td>${esc(item.referenceNumber || '—')}</td>
          <td style="text-align:center;">${esc(item.quantity)}</td>
        </tr>`;
    }).join('');

    const pspBlock = info ? `
      <div class="psp-block">
        ${info.pspNames?.length ? `<div><strong>PSP:</strong> ${esc(info.pspNames.join(', '))}</div>` : ''}
        ${info.shipmentGroups?.length ? `<div><strong>Shipment group:</strong> ${esc(info.shipmentGroups.join(', '))}</div>` : ''}
        ${info.batches?.length ? `<div><strong>PSP batch:</strong> ${esc(info.batches.join(', '))}</div>` : ''}
        ${(info.tracking || []).map(t => `<div><strong>Tracking:</strong> ${esc(t.carrier || '')} ${esc(t.trackingNumber)}${t.date ? ` <span class="muted">(${esc(new Date(t.date).toLocaleDateString())})</span>` : ''}</div>`).join('')}
        ${(info.slipData || []).map(sd => {
          const lines = slipDataLines(sd.data);
          return lines.length ? `<div style="margin-top:4px;"><strong>PSP slip details (${esc(sd.batch)}):</strong><div class="slipdata">${lines.join('<br/>')}</div></div>` : '';
        }).join('')}
      </div>` : '';

    const notOnSlip = info?.notOnSlip?.length ? `
      <div class="warn">⚠ Not on this slip (${info.notOnSlip.length}): ${info.notOnSlip.map(x => `${esc(x.description)} <span class="muted">(${esc(String(x.status || '').replace(/_/g, ' '))})</span>`).join('; ')}</div>` : '';

    return `
      <div class="order-page">
        ${slipHeader(idx)}
        <div class="order-block">
          <div style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:10px;">
            <div>
              <h2 style="margin:0 0 4px;">Order: ${esc(orderNum)}</h2>
              <p><strong>Company:</strong> ${esc(order.companyName)} · ${esc(order.groupName)}</p>
              ${customer ? `<p><strong>Customer:</strong> ${esc(customer)}</p>` : ''}
              ${order.roomNumber ? `<p><strong>Room:</strong> ${esc(order.roomNumber)}</p>` : ''}
              ${order.createdAt ? `<p><strong>Order date:</strong> ${esc(new Date(order.createdAt).toLocaleDateString())}</p>` : ''}
            </div>
            <div style="text-align:center;">
              <svg class="barcode" data-value="${esc(orderNum)}"></svg>
              <div style="font-size:10px;color:#666;margin-top:2px;">${esc(orderNum)}</div>
            </div>
          </div>
          ${pspBlock}
          <table>
            <thead>
              <tr><th>✓</th><th>Photo</th><th>Item ID</th><th>Product</th><th>SKU</th><th>Ref #</th><th>Qty</th></tr>
            </thead>
            <tbody>${itemRows}</tbody>
          </table>
          ${notOnSlip}
          <div class="receiving">
            <div><strong>Received by:</strong> <span class="line"></span></div>
            <div><strong>Date:</strong> <span class="line short"></span></div>
            <div style="flex-basis:100%;"><strong>Notes / condition:</strong> <span class="line wide"></span></div>
          </div>
        </div>
      </div>`;
  }).join('');

  const html = `<!DOCTYPE html><html><head><title>Packing Slip ${esc(slipId)}</title>
    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
    <style>
      body { font-family: Arial, sans-serif; padding: 24px; font-size: 13px; }
      h2 { margin: 0 0 8px; font-size: 16px; }
      p { margin: 2px 0; }
      table { width: 100%; border-collapse: collapse; margin-top: 8px; }
      td, th { padding: 6px 8px; border: 1px solid #ddd; font-size: 12px; vertical-align: middle; }
      th { background: #f5f5f5; font-weight: 600; }
      .muted { color:#666; font-size:11px; margin-top:2px; }
      .reprint { font-size:11px; color:#b45309; border:1px solid #b45309; border-radius:4px; padding:1px 6px; vertical-align:middle; }
      .slip-header { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:16px; border-bottom:2px solid #333; margin-bottom:20px; }
      .psp-block { background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; padding:8px 10px; margin:6px 0 10px; font-size:12px; line-height:1.6; }
      .slipdata { font-size:11px; color:#333; margin-left:8px; }
      .box { display:inline-block; width:14px; height:14px; border:1.5px solid #333; border-radius:2px; }
      .warn { margin-top:10px; padding:6px 10px; border:1px solid #f59e0b; background:#fffbeb; border-radius:6px; font-size:12px; }
      .receiving { display:flex; flex-wrap:wrap; gap:10px 32px; margin-top:18px; padding-top:12px; border-top:1px dashed #999; font-size:12px; }
      .line { display:inline-block; width:220px; border-bottom:1px solid #333; height:14px; vertical-align:bottom; }
      .line.short { width:120px; } .line.wide { width:520px; }
      .order-page { page-break-after: always; break-after: page; }
      .order-page:last-child { page-break-after: auto; break-after: auto; }
      .order-page + .order-page { page-break-before: always; break-before: page; }
      @media screen { .order-page { border-bottom: 3px dashed #bbb; padding-bottom: 24px; margin-bottom: 32px; } .order-page:last-child { border-bottom: none; } }
    </style>
    </head><body>
    ${pages}
    <script>
      window.onload = function() {
        document.querySelectorAll('.slip-barcode').forEach(function(el) {
          JsBarcode(el, ${JSON.stringify(String(slipId))}, { format:'CODE128', width:1.5, height:50, displayValue:false });
        });
        document.querySelectorAll('.barcode[data-value]').forEach(function(el) {
          JsBarcode(el, el.getAttribute('data-value'), { format:'CODE128', width:1.5, height:40, displayValue:false });
        });
        ${opts.autoPrint === false ? '' : 'window.print();'}
      };
    <\/script>
    </body></html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
}

// Opens a placeholder window synchronously (inside the click handler) so the
// browser doesn't block it; fill it later with printPackingSlip(..., { win }).
export function openSlipWindow() {
  const win = window.open('', '_blank');
  if (win) win.document.write('<p style="font-family:Arial;padding:24px;color:#666">Preparing packing slip…</p>');
  return win;
}
